import type { Demanda } from '../../../models';

/**
 * Utilitário para encontrar demandas relacionadas baseado em critérios
 */
export const findRelatedDemandas = (
  currentDemanda: Demanda,
  allDemandas: Demanda[],
  maxResults: number = 10
): Demanda[] => {
  if (!currentDemanda || !allDemandas.length) return [];

  const related: Array<{ demanda: Demanda; score: number }> = [];

  allDemandas.forEach(demanda => {
    // Não incluir a demanda atual
    if (demanda.id === currentDemanda.id) return;

    let score = 0;

    // Critério 1: Mesmo setor (peso 3)
    if (demanda.setor?.id === currentDemanda.setor?.id) {
      score += 3;
    }

    // Critério 2: Mesmo processo (peso 2)
    if (demanda.processo?.id === currentDemanda.processo?.id) {
      score += 2;
    }

    // Critério 3: Mesmo demandante (peso 2)
    if (demanda.demandante?.id === currentDemanda.demandante?.id) {
      score += 2;
    }

    // Critério 4: Mesmo demandado/atribuído (peso 1)
    if (demanda.demandado?.id === currentDemanda.demandado?.id) {
      score += 1;
    }

    // Critério 5: Palavras-chave similares no título (peso 1)
    if (currentDemanda.titulo && demanda.titulo) {
      const currentWords = currentDemanda.titulo.toLowerCase().split(' ').filter(w => w.length > 3);
      const demandaWords = demanda.titulo.toLowerCase().split(' ').filter(w => w.length > 3);
      const commonWords = currentWords.filter(word => demandaWords.includes(word));
      if (commonWords.length > 0) {
        score += Math.min(commonWords.length * 0.5, 1);
      }
    }

    // Critério 6: Status relacionados (peso 0.5)
    const relatedStatuses = {
      'EMATRIBUICAO': ['ATRIBUIDA', 'EMPROGRESSO'],
      'ATRIBUIDA': ['EMATRIBUICAO', 'EMPROGRESSO'],
      'EMPROGRESSO': ['ATRIBUIDA', 'SOLUCIONADA'],
      'SOLUCIONADA': ['EMPROGRESSO', 'FINALIZADA'],
      'FINALIZADA': ['SOLUCIONADA']
    };
    
    if (currentDemanda.status && demanda.status) {
      const related = relatedStatuses[currentDemanda.status as keyof typeof relatedStatuses];
      if (related?.includes(demanda.status)) {
        score += 0.5;
      }
    }

    // Critério 7: Prioridade similar (peso 0.3)
    if (demanda.prioridade === currentDemanda.prioridade) {
      score += 0.3;
    }

    // Critério 8: Criadas no mesmo período (peso 0.2)
    if (currentDemanda.created_at && demanda.created_at) {
      const currentDate = new Date(currentDemanda.created_at);
      const demandaDate = new Date(demanda.created_at);
      const daysDiff = Math.abs(currentDate.getTime() - demandaDate.getTime()) / (1000 * 60 * 60 * 24);
      
      if (daysDiff <= 7) { // Mesma semana
        score += 0.2;
      } else if (daysDiff <= 30) { // Mesmo mês
        score += 0.1;
      }
    }

    // Só incluir se tiver alguma relação (score > 0)
    if (score > 0) {
      related.push({ demanda, score });
    }
  });

  // Ordenar por score (maior para menor) e retornar as top demandas
  return related
    .sort((a, b) => b.score - a.score)
    .slice(0, maxResults)
    .map(item => item.demanda);
};

/**
 * Tipos de relação para categorizar demandas relacionadas
 */
export const RelationType = {
  SAME_SECTOR: 'Mesmo Setor',
  SAME_PROCESS: 'Mesmo Processo', 
  SAME_REQUESTER: 'Mesmo Demandante',
  SAME_ASSIGNEE: 'Mesmo Responsável',
  SIMILAR_KEYWORDS: 'Palavras-chave Similares',
  RELATED_STATUS: 'Status Relacionado',
  SAME_PRIORITY: 'Mesma Prioridade',
  SAME_PERIOD: 'Mesmo Período'
} as const;

export type RelationType = typeof RelationType[keyof typeof RelationType];

/**
 * Função mais avançada que retorna demandas relacionadas com tipo de relação
 */
export const findRelatedDemandasWithType = (
  currentDemanda: Demanda,
  allDemandas: Demanda[],
  maxResults: number = 10
): Array<{ demanda: Demanda; relationType: RelationType[]; score: number }> => {
  if (!currentDemanda || !allDemandas.length) return [];

  const related: Array<{ demanda: Demanda; relationType: RelationType[]; score: number }> = [];

  allDemandas.forEach(demanda => {
    if (demanda.id === currentDemanda.id) return;

    let score = 0;
    const relationTypes: RelationType[] = [];

    // Aplicar mesma lógica mas guardando os tipos de relação
    if (demanda.setor?.id === currentDemanda.setor?.id) {
      score += 3;
      relationTypes.push(RelationType.SAME_SECTOR);
    }

    if (demanda.processo?.id === currentDemanda.processo?.id) {
      score += 2;
      relationTypes.push(RelationType.SAME_PROCESS);
    }

    if (demanda.demandante?.id === currentDemanda.demandante?.id) {
      score += 2;
      relationTypes.push(RelationType.SAME_REQUESTER);
    }

    if (demanda.demandado?.id === currentDemanda.demandado?.id) {
      score += 1;
      relationTypes.push(RelationType.SAME_ASSIGNEE);
    }

    // Verificar palavras-chave similares
    if (currentDemanda.titulo && demanda.titulo) {
      const currentWords = currentDemanda.titulo.toLowerCase().split(' ').filter(w => w.length > 3);
      const demandaWords = demanda.titulo.toLowerCase().split(' ').filter(w => w.length > 3);
      const commonWords = currentWords.filter(word => demandaWords.includes(word));
      if (commonWords.length > 0) {
        score += Math.min(commonWords.length * 0.5, 1);
        relationTypes.push(RelationType.SIMILAR_KEYWORDS);
      }
    }

    if (score > 0) {
      related.push({ demanda, relationType: relationTypes, score });
    }
  });

  return related
    .sort((a, b) => b.score - a.score)
    .slice(0, maxResults);
};